import { readJson, throwProblem } from '@/shared/api/problem';
import { env } from '@/shared/config/env';

/**
 * API аутентификации сотрудника (B-STF-04, ADR-0002).
 *
 * Типы описаны руками по контракту `/api/v1/admin/auth/*`: снимок OpenAPI в репозитории пуст —
 * спека админки закрыта аутентификацией, и `pnpm api:sync` без токена сотрудника её не забирает.
 * После синхронизации спеки файл должен перейти на сгенерированные типы `@/shared/api/schema`.
 *
 * Токен сессии фронтенд не хранит и не читает: он приходит в httpOnly-cookie, которую браузер
 * прикладывает сам. Поле `token` в ответе входа предназначено клиентам с `Authorization: Bearer`
 * и здесь намеренно игнорируется — записать его в localStorage значило бы отдать сессию любому
 * скрипту на странице.
 */

/** Роли из FR-ADM-05: ровно три, настраиваемых прав нет. */
export type StaffRole = 'ADMINISTRATOR' | 'ORDER_MANAGER' | 'CONTENT_MANAGER';

export interface StaffMember {
  id: string;
  email: string;
  fullName: string;
  role: StaffRole;
  status: 'ACTIVE' | 'BLOCKED';
  /** Пароль назначен администратором или бутстрапом — до смены работать нельзя. */
  mustChangePassword: boolean;
}

/**
 * Ответ входа. Сотрудника целиком отсюда не берём — карточку даёт `/me`, единственный
 * источник правды о сессии; здесь важен только маршрут после входа.
 */
export interface LoginResult {
  mustChangePassword: boolean;
}

const BASE = `${env.apiBaseUrl}/v1/admin/auth`;

/** Требования к паролю из PasswordPolicy бэкенда — дублируются, чтобы не ловить 400 на очевидном. */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_BYTES = 72;

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await fetch(`${BASE}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    await throwProblem(response);
  }
  // Cookie выставлена заголовком ответа, даже если тело не дошло (обрыв, прокси). Считать это
  // сбоем нельзя: сотрудник вошёл. Непрочитанный признак смены пароля не потеряется — его
  // всё равно проверяет защита разделов по ответу `/me`.
  const body = await readJson<LoginResult>(response);
  return { mustChangePassword: body?.mustChangePassword ?? false };
}

export async function fetchMe(): Promise<StaffMember> {
  const response = await fetch(`${BASE}/me`, { credentials: 'include' });
  if (!response.ok) {
    await throwProblem(response);
  }
  return (await response.json()) as StaffMember;
}

export async function logout(): Promise<void> {
  const response = await fetch(`${BASE}/logout`, { method: 'POST', credentials: 'include' });
  // 401 на выходе — сессии уже нет, то есть цель достигнута. Остальные отказы значимы:
  // сотрудник должен узнать, что сессия на сервере жива.
  if (!response.ok && response.status !== 401) {
    await throwProblem(response);
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const response = await fetch(`${BASE}/password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!response.ok) {
    await throwProblem(response);
  }
}
